import React from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Building2, FileText, Eye, Upload } from 'lucide-react';
import { ProposalStatusBadge } from './ProposalStatusBadge';
import { ProposalActionsMenu } from './ProposalActionsMenu';

const ProposalDriveAttachment = ({ proposal }) => {
  if (!proposal.drive_file_name) return null;
  return (
    <div className="mb-4 p-2 bg-muted rounded flex items-center gap-2">
      <FileText className="h-4 w-4 text-muted-foreground" />
      <span className="text-xs truncate">{proposal.drive_file_name}</span>
      {proposal.drive_file_link && (
        <a href={proposal.drive_file_link} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
          <Eye className="h-4 w-4 text-muted-foreground hover:text-foreground" />
        </a>
      )}
    </div>
  );
};

const ProposalAmountBlock = ({ proposal }) => {
  if (!proposal.amount && !proposal.total_area_sqmt) return null;
  return (
    <div className="mb-4 pb-4 border-b space-y-1">
      {proposal.amount && (
        <>
          <p className="text-sm text-muted-foreground">Proposed Amount</p>
          <p className="text-lg font-mono font-semibold">INR {proposal.amount.toLocaleString('en-IN')}</p>
        </>
      )}
      {proposal.total_area_sqmt && (
        <p className="text-xs text-muted-foreground">Area: {proposal.total_area_sqmt.toLocaleString()} sq.ft.</p>
      )}
    </div>
  );
};

const triggerUpload = (proposal, onUpload) => {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = '.pdf,.doc,.docx';
  input.onchange = (e) => {
    if (e.target.files[0]) onUpload(proposal.proposal_id, e.target.files[0]);
  };
  input.click();
};

const ProposalCard = ({ proposal, onView, onStatusChange, onConvert, onDelete, onUploadPdf }) => (
  <Card
    className="p-6 hover:shadow-md transition-shadow cursor-pointer"
    data-testid={`proposal-card-${proposal.proposal_id}`}
    onClick={() => onView(proposal)}
  >
    <div className="flex items-start justify-between mb-4">
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-2">
          <h3 className="text-xl font-heading font-semibold">{proposal.title}</h3>
          <span className="text-xs text-muted-foreground">v{proposal.version || 1}</span>
        </div>
        <p className="text-sm text-muted-foreground mb-1">Client: {proposal.client_name}</p>
        {proposal.company_name && (
          <p className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
            <Building2 className="h-3 w-3" />{proposal.company_name}
          </p>
        )}
        {proposal.category && (
          <p className="text-xs text-muted-foreground">{proposal.category}</p>
        )}
      </div>
      <ProposalStatusBadge status={proposal.status} />
    </div>
    <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{proposal.description}</p>

    <ProposalDriveAttachment proposal={proposal} />
    <ProposalAmountBlock proposal={proposal} />

    <div className="flex items-center justify-between">
      <div className="flex gap-2 flex-wrap" onClick={(e) => e.stopPropagation()}>
        <Button
          size="sm"
          variant="outline"
          onClick={() => triggerUpload(proposal, onUploadPdf)}
          data-testid={`attach-pdf-${proposal.proposal_id}`}
        >
          <Upload className="mr-1 h-3 w-3" />
          Attach PDF
        </Button>
      </div>
      <div onClick={(e) => e.stopPropagation()}>
        <ProposalActionsMenu
          proposal={proposal}
          onView={onView}
          onStatusChange={onStatusChange}
          onConvert={onConvert}
          onDelete={onDelete}
          testIdPrefix="tile-actions"
        />
      </div>
    </div>
  </Card>
);

export const ProposalTileView = ({ proposals, onView, onStatusChange, onConvert, onDelete, onUploadPdf }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
    {proposals.map((proposal) => (
      <ProposalCard
        key={proposal.proposal_id}
        proposal={proposal}
        onView={onView}
        onStatusChange={onStatusChange}
        onConvert={onConvert}
        onDelete={onDelete}
        onUploadPdf={onUploadPdf}
      />
    ))}
  </div>
);
